import React, { useState } from 'react';
import styled from 'styled-components';
import PieChart from '../components/pieChart';
import StackedAreaChart from '../components/stackedAreaChart';
import GroupMemberActivities from '../components/groupMemberActivities';
import { formatScore, formatPeriodLabel } from '../helpers/functions';

const StyledPieChart = styled.div`
    width:100%;
    height:400px;
    display:flex;
    position:relative;
    align-items:center;
    flex-flow:row nowrap;
    @media(max-width:1000px){
      flex-flow: column;
      height:unset;
    }
`;

const StyledContainer = styled.div`
    width: 100%;
    height: 100%;
    text-align: center;
`;

const PieColumn = styled.div`
    display:flex;
    flex-direction:column;
    align-items:center;
    flex-shrink:0;
`;

const PeriodNav = styled.div`
    display:flex;
    align-items:center;
    justify-content:center;
    gap:10px;
    margin-top:-40px;
    position:relative;
    z-index:5;
`;

const NavBtn = styled.button`
    display:flex;
    align-items:center;
    justify-content:center;
    width:22px;
    height:22px;
    border:none;
    border-radius:50%;
    font-size:0.85rem;
    line-height:1;
    cursor:pointer;
    color:var(--text);
    background:var(--color-background);
    transition:0.2s;
    &:hover:not(:disabled){ background:var(--primary); }
    &:disabled{ opacity:0.35; cursor:default; }
`;

const PeriodLabel = styled.span`
    min-width:110px;
    font-weight:600;
    font-size:0.8rem;
    text-transform:capitalize;
    color:var(--text);
`;

const StyledStatsHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 40px;
    gap: 16px;
`;

const StyledStatsTitle = styled.h2`
    font-size: 1.2rem;
    text-align: left;
    pointer-events: none;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color:var(--text);
    @media(max-width:570px){
      font-size: 0.95rem;
      flex-shrink: 0;
    }
`;

const UserLegend = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    overflow-x: auto;
    margin-right:20px;
    padding: 8px 0;
    cursor:default;
    &::-webkit-scrollbar { height: 3px; }
    &::-webkit-scrollbar:hover { height: 3px; }
    &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 2px; }
    @media(max-width:570px){
      gap: 8px;
      margin-right: 10px;
      flex: 1;
      min-width: 0;
      justify-content: flex-end;
    }
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    flex-shrink: 0;
`;

const LegendDot = styled.span`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
`;

const LegendName = styled.span`
    font-size: 0.85rem;
    color: var(--text-inactive);
    @media(max-width:570px){
      font-size: 0.72rem;
    }
`;

const StatsList = styled.div`
    flex:1;
    display:flex;
    flex-direction:column;
    gap:8px;
    padding:0 10px;
    max-height:100%;
    overflow-y:auto;
    @media(max-width:1000px){
      width:100%;
      margin-top:20px;
    }
`;

const StatRow = styled.div`
    display:flex;
    align-items:center;
    gap:10px;
    padding:10px 14px;
    border-radius:6px;
    cursor:pointer;
    background-color: var(--color-background);
    border:1px solid transparent;
    transition:0.2s;
    &:hover{
      background-color: var(--primary);
    }
`;

const Dot = styled.span`
    width:14px;
    height:14px;
    border-radius:50%;
    flex-shrink:0;
`;

const Name = styled.span`
    flex:1;
    min-width:0;
    text-align:left;
    overflow:hidden;
    text-overflow:ellipsis;
    white-space:nowrap;
    font-weight:600;
    color: var(--text);
`;

const Time = styled.span`
    margin-left:auto;
    font-weight:600;
    font-size: 0.8rem;
    color: var(--text);
`;

const Details = styled.div`
    flex:1;
    padding:16px;
    border-radius:6px;
    background-color:rgba(255,255,255,0.06);
    @media(max-width:1000px){
      width:100%;
      margin-top:20px;
    }
`;

const DetailsHeader = styled.div`
    display:flex;
    align-items:center;
    justify-content:space-between;
    margin-bottom:10px;
    font-size:1.2rem;
    font-weight:600;
    color:var(--text);
`;

const DetailsTitle = styled.span`
    flex:1;
    min-width:0;
    margin-right:10px;
    text-align:left;
    overflow:hidden;
    text-overflow:ellipsis;
    white-space:nowrap;
`;

const CloseBtn = styled.span`
    cursor:pointer;
    display:flex;
    align-items:center;
    justify-content:center;
    gap:5px;
    height:30px;
    padding:0 12px;
    flex-shrink:0;
    font-size:0.85rem;
    font-weight:600;
    line-height:1;
    border-radius:20px;
    color:var(--light);
    background:var(--text-inactive);
    transition:0.2s;
    > span{
        font-size:1.1rem;
    }
    &:hover{
    background:var(--text);
    }
`;

const EmptyState = styled.div`
    width:100%;
    display:flex;
    flex-direction:column;
    align-items:center;
    justify-content:center;
    text-align:center;
    padding:80px 20px;
    gap:10px;
`;

const EmptyTitle = styled.h2`
    font-size:1.3rem;
    color:var(--text);
    margin:0;
`;

const EmptyText = styled.p`
    font-size:0.95rem;
    color:var(--text-inactive);
    margin:0;
    max-width:360px;
    line-height:1.5;
`;

function Group({ hasGroup, statsData, activityTypes, users, goal, goalPeriod, scoringMode, usersActivities, periodOffset, setPeriodOffset }) {
    const areaChartWidth = 860;
    const areaChartHeight = 470;
    const [selected, setSelected] = useState(null);

    if (!hasGroup) {
        return (
            <EmptyState>
                <EmptyTitle>Nie należysz do żadnej grupy</EmptyTitle>
                <EmptyText>
                    Utwórz nową grupę albo dołącz do istniejącej przez link zapraszający.
                    Zrobisz to w ustawieniach profilu.
                </EmptyText>
            </EmptyState>
        );
    }


    const calculateAmount = (d) => d.activities.reduce((total, a) => total + (scoringMode === 'points' ? a.time * a.weight / 60 : a.time), 0);

    // Wszyscy członkowie grupy - także ci bez aktywności (0 minut), posortowani malejąco po czasie
    const members = users.map((u) => {
        const d = usersActivities.find((ua) => ua.user_id === u.user_id);
        const activities = d ? [...d.activities].sort((a, b) => (a.date < b.date ? 1 : -1)) : [];
        return {
            user_id: u.user_id,
            name: u.user_name,
            color: '#' + u.user_color,
            amount: d ? calculateAmount(d) : 0,
            activities,
        };
    }).sort((a, b) => b.amount - a.amount);

    const changePeriod = (delta) => {
        setSelected(null);
        setPeriodOffset((prev) => Math.max(0, prev + delta));
    };

    return (
        <StyledContainer>
            <StyledPieChart>
                <PieColumn>
                    <PieChart
                        goal={goal}
                        goalPeriod={goalPeriod}
                        scoringMode={scoringMode}
                        users={users}
                        usersActivities={usersActivities}
                        setComponent={setSelected}
                        selected={selected}
                        isCurrent={periodOffset === 0}
                    />
                    <PeriodNav>
                        <NavBtn onClick={() => changePeriod(1)} aria-label="Poprzedni okres">‹</NavBtn>
                        <PeriodLabel>{formatPeriodLabel(goalPeriod, periodOffset)}</PeriodLabel>
                        <NavBtn onClick={() => changePeriod(-1)} disabled={periodOffset === 0} aria-label="Następny okres">›</NavBtn>
                    </PeriodNav>
                </PieColumn>
                {selected ? (
                    <Details>
                        <DetailsHeader>
                            <DetailsTitle>{selected.name}</DetailsTitle>
                            <CloseBtn onClick={() => setSelected(null)}>Zamknij <span>×</span></CloseBtn>
                        </DetailsHeader>
                        <GroupMemberActivities
                            activityTypes={activityTypes}
                            scoringMode={scoringMode}
                            summary={selected.amount}
                            activities={selected.activities}
                        />
                    </Details>
                ) : (
                    <StatsList>
                        {members.map((m) => (
                            <StatRow key={m.user_id} onClick={() => setSelected(m)}>
                                <Dot style={{ backgroundColor: m.color }} />
                                <Name>{m.name}</Name>
                                <Time>{formatScore(m.amount, scoringMode)}</Time>
                            </StatRow>
                        ))}
                    </StatsList>
                )}
            </StyledPieChart>

            <StyledStatsHeader>
                <StyledStatsTitle>Statystyki</StyledStatsTitle>
                <UserLegend>
                    {(selected ? members.filter(m => m.user_id === selected.user_id) : members).map(m => (
                        <LegendItem key={m.user_id}>
                            <LegendDot style={{ backgroundColor: m.color }} />
                            <LegendName>{m.name}</LegendName>
                        </LegendItem>
                    ))}
                </UserLegend>
            </StyledStatsHeader>
            <StackedAreaChart
                data={statsData}
                goalPeriod={goalPeriod}
                width={areaChartWidth}
                height={areaChartHeight}
                users={users}
                selectedUserId={selected ? selected.user_id : null}
            />
        </StyledContainer>
    );
}

export default Group;
